import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { withAuth } from "../../hoc";
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import { getMember } from "../../services/MembersService";
import {
  capitalizeFirstLetter,
  getMemberType,
  getReadableDateString,
} from "../../utils/commons";
import AddUserDrawer from "./AddMember";
import { ViewMember } from "./ViewMembers";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  memberType: string;
  active: string;
  photoUrl: string;
  createdAt: string;
}

const Members = () => {
  const location = useLocation() as { state?: { selectedMemberId?: string } };

  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [usersPerPage] = useState<number>(10);
  const [currentRole, setCurrentRole] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [userCount, setUserCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [showView, setShowView] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<string>("");
  const theme = useTheme();

  useEffect(() => {
    const selectedMemberId = location.state?.selectedMemberId;
    if (selectedMemberId) {
      setSelectedMember(selectedMemberId);
      setShowView(true);
    }
  }, [location.state]);

  useEffect(() => {
    fetchMembers(1);
  }, [currentRole, showView]);

  useEffect(() => {
    fetchMembers(currentPage);
  }, [currentPage]);

  const fetchMembers = async (page: number) => {
    setLoading(true);
    setCurrentPage(page);
    try {
      let res = await getMember({
        page,
        size: usersPerPage,
        role: currentRole,
      });

      setUsers(res.content);
      setUserCount(res.totalElements);
      setTotalPages(res.totalPages);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentRole(newValue);
  };

  // Filter users based on selected role and search query
  const filteredUsers = users.filter((user) => {
    const roleMatch =
      currentRole !== "ALL" ? user.memberType === currentRole : true;
    const searchMatch = `${user.firstName} ${user.lastName}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return roleMatch && searchMatch;
  });

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const openDrawer = () => {
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      {showView && (
        <ViewMember setShowView={setShowView} selectedMember={selectedMember} />
      )}
      {!showView && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "90.5vh",
            padding: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                marginBottom: theme.spacing(2),
                color: theme.palette.text.primary,
              }}
            >
              User List
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={openDrawer}
              startIcon={<AddRoundedIcon />}
            >
              Add User
            </Button>
            <AddUserDrawer
              open={drawerOpen}
              onClose={closeDrawer}
              fetchMembers={fetchMembers}
              setCurrentRole={setCurrentRole}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <Tabs value={currentRole} onChange={handleTabChange}>
              <Tab value="ALL" label="All" sx={{ minWidth: "initial" }} />
              <Tab
                value="VISITOR"
                label="Visitor"
                sx={{ minWidth: "initial" }}
              />
              <Tab
                value="SUBSCRIBER"
                label="Subscriber"
                sx={{ minWidth: "initial" }}
              />
              <Tab
                value="GYM_MEMBER"
                label="Gym Member"
                sx={{ minWidth: "initial" }}
              />
              <Tab
                value="ONLINE_MEMBER"
                label="Online Member"
                sx={{ minWidth: "initial" }}
              />
            </Tabs>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                aria-label="filter"
                size="small"
                sx={{ marginRight: 1 }}
              >
                {/* <FilterListIcon /> */}
              </IconButton>
              <TextField
                placeholder="Search by name"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <TableContainer sx={{ height: 100 }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.info.light,
                  }}
                >
                  {/* Header cells */}
                  <TableCell
                    style={{
                      width: 80,
                      minWidth: 80,
                      maxWidth: 80,
                    }}
                    align="center"
                  ></TableCell>
                  <TableCell
                    style={{
                      width: 200,
                      minWidth: 200,
                      maxWidth: 200,
                    }}
                    align="center"
                  >
                    Full Name
                  </TableCell>
                  <TableCell
                    style={{
                      width: 170,
                      minWidth: 170,
                      maxWidth: 170,
                    }}
                    align="center"
                  >
                    Phone
                  </TableCell>
                  <TableCell
                    style={{
                      width: 200,
                      minWidth: 200,
                      maxWidth: 200,
                    }}
                    align="center"
                  >
                    Email
                  </TableCell>
                  <TableCell
                    style={{
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                    }}
                    align="center"
                  >
                    Joined on
                  </TableCell>
                  <TableCell
                    style={{
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                    }}
                    align="center"
                  >
                    Member Type
                  </TableCell>
                  <TableCell
                    style={{
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                    }}
                    align="center"
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              scrollbarWidth: "none",
            }}
          >
            <UserListTable
              loading={loading}
              users={filteredUsers}
              usersPerPage={usersPerPage}
              setShowView={setShowView}
              setSelectedMember={setSelectedMember}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "auto",
              marginBottom: 0,
              color: theme.palette.text.secondary,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ marginRight: 1 }}>
                Users Count: <b>{userCount}</b>&nbsp;,
              </Typography>
              <Typography variant="body2">
                Page <b>{currentPage}</b> of {totalPages}
              </Typography>
            </Box>

            <Box>
              <Button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

function UserListTable({
  loading,
  users,
  usersPerPage,
  setShowView,
  setSelectedMember,
}: {
  loading: boolean;
  users: User[];
  usersPerPage: number;
  setShowView: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedMember: React.Dispatch<React.SetStateAction<string>>;
}) {
  const theme = useTheme();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "success";
      case "Inactive":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <>
      {/*  Table Headerrow That Should be Sticky */}

      {/* Table body rows that should be scollbar */}
      <div>
        <TableContainer>
          <Table>
            <TableBody>
              {loading ? (
                // Skeleton loader when loading data
                <>
                  {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell
                        style={{ width: 80, minWidth: 80, maxWidth: 80 }}
                        align="center"
                      >
                        <Skeleton variant="circular" width={32} height={32} />
                      </TableCell>

                      <TableCell
                        style={{ width: 200, minWidth: 200, maxWidth: 200 }}
                        align="center"
                      >
                        <Skeleton variant="rectangular" height={35} />
                      </TableCell>
                      <TableCell
                        style={{ width: 170, minWidth: 170, maxWidth: 170 }}
                        align="center"
                      >
                        <Skeleton variant="rectangular" height={35} />
                      </TableCell>
                      <TableCell
                        style={{ width: 200, minWidth: 200, maxWidth: 200 }}
                        align="center"
                      >
                        <Skeleton variant="rectangular" height={35} />
                      </TableCell>
                      <TableCell
                        style={{
                          width: 120,
                          minWidth: 120,
                          maxWidth: 120,
                        }}
                        align="center"
                      >
                        <Skeleton variant="rectangular" height={35} />
                      </TableCell>

                      <TableCell
                        style={{ width: 150, minWidth: 150, maxWidth: 150 }}
                        align="center"
                      >
                        <Skeleton variant="rectangular" height={35} />
                      </TableCell>
                      <TableCell
                        style={{ width: 150, minWidth: 150, maxWidth: 150 }}
                        align="center"
                      >
                        <Skeleton variant="rectangular" height={25} />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : (
                // Render actual user data when not loading
                users.map((user) => (
                  <TableRow
                    onClick={() => {
                      setSelectedMember(user.id);
                      setShowView(true);
                    }}
                    key={user.id}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell
                      style={{ width: 80, minWidth: 80, maxWidth: 80 }}
                      align="center"
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          backgroundColor: theme.palette.info.light,
                          borderRadius: "50%",
                          marginLeft: 15,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: theme.palette.secondary.main,
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                      >
                        {user.firstName?.charAt(0).toUpperCase()}
                        {user.lastName?.charAt(0).toUpperCase()}
                      </div>
                    </TableCell>
                    <TableCell
                      style={{
                        width: 200,
                        minWidth: 200,
                        maxWidth: 200,
                        paddingLeft: 30,
                      }}
                      align="left"
                    >
                      {user.firstName && user.lastName
                        ? `${capitalizeFirstLetter(
                            user.firstName
                          )} ${capitalizeFirstLetter(user.lastName)}`
                        : "N/A"}
                    </TableCell>

                    <TableCell
                      style={{ width: 170, minWidth: 170, maxWidth: 170 }}
                      align="center"
                    >
                      {"+91-" + user.phone}
                    </TableCell>
                    <TableCell
                      style={{
                        width: 200,
                        minWidth: 200,
                        maxWidth: 200,
                        paddingLeft: 20,
                      }}
                      align="left"
                    >
                      {user.email?.toLowerCase() || "N/A"}
                    </TableCell>
                    <TableCell
                      style={{
                        width: 120,
                        minWidth: 120,
                        maxWidth: 120,
                      }}
                      align="left"
                    >
                      {getReadableDateString(user.createdAt) || "N/A"}
                    </TableCell>

                    <TableCell
                      style={{ width: 150, minWidth: 150, maxWidth: 150 }}
                      align="center"
                    >
                      {getMemberType(user.memberType)}
                    </TableCell>
                    <TableCell
                      style={{ width: 150, minWidth: 150, maxWidth: 150 }}
                      align="center"
                    >
                      <Chip
                        label={user.active ? "Active" : "Inactive"}
                        size="small"
                        color={getStatusColor(
                          user.active ? "Active" : "Inactive"
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
}
export default withAuth(Members);
